import React, { useState, memo } from 'react'
import {
    Card, CardContent, Typography, Button,
    Box
} from '@material-ui/core'
import GestureIcon from '@material-ui/icons/Gesture'
import DescriptionIcon from '@material-ui/icons/Description'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import SearchIcon from '@material-ui/icons/Search'
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff'
import ArchiveIcon from '@material-ui/icons/Archive'
import CancelIcon from '@material-ui/icons/Cancel'
import BuildIcon from '@material-ui/icons/Build'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import PortalSignModal from './PortalSignModal'
import { dark } from './portalStyles'
import { translations } from './portalI18n'
import showPDFSignUseCase from 'tasks/services/sign/showPDFSignUseCase'
import { getTaskDetail } from './portalApi'

const renderDate = (ts, lang) => {
    if (!ts) return '—'
    return new Date(ts).toLocaleDateString(lang === 'en' ? 'en-GB' : 'es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const stateColors = { 0: '#FA4C4C', 1: '#D07F59', 2: '#4cc488', 3: '#d4d457', 4: '#c3c3c3', 5: '#ff9966', 6: '#87CEEB', 7: '#b39ddb' }
const stateIcons  = { 0: SearchIcon, 1: FlightTakeoffIcon, 2: CheckCircleIcon, 3: ArchiveIcon, 4: CancelIcon, 5: BuildIcon, 6: HourglassEmptyIcon, 7: LocalShippingIcon }
const IS_MOBILE = window.innerWidth <= 600

const PortalTaskCard = ({ task, lang = 'es', client, taskInvoiceMap = {}, onOpenInvoice }) => {
    const t = translations[lang]
    const sinReparar = Number(task.state) === 4
    const hasBudget = !sinReparar && !!task.budget && Number(task.budget) > 0
    const [showSign, setShowSign] = useState(false)
    const [justSigned, setJustSigned] = useState(false)
    const signed = justSigned || !!task.clientPortalSign || !!task.presupuestoAceptado || task.budgetAccept === 1
    // Estado 1 (Enviado al exterior) se muestra como 5 (Reparando) al cliente
    const displayState = Number(task.state) === 1 ? 5 : Number(task.state)
    const stateLabel = t.states[displayState] || t.unknownState
    const stateColor = stateColors[displayState] || '#555'
    const StateIcon  = stateIcons[displayState] || null
    const [loadingPDF, setLoadingPDF] = useState(false)

    // Lookup O(1) — cubre referencias principales y líneas secundarias de facturas multi-tarea
    const invoice = taskInvoiceMap[task.reference] || null

    const handleVerInforme = async () => {
        setLoadingPDF(true)
        try {
            const detailData = await getTaskDetail(task.id)
            await showPDFSignUseCase(task, client, detailData, 3, null)
        } catch (err) {
            console.error('Error al generar informe', err)
        } finally {
            setLoadingPDF(false)
        }
    }

    return (
        <>
            <Card style={{
                marginBottom: 6, borderRadius: 8,
                background: dark.surface,
                border: `2px solid ${stateColor}`,
            }} elevation={0}>
                <CardContent style={{ padding: '10px 14px 10px' }}>
                    {/* Header: referencia + fecha entrada */}
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
                        <Box style={{ background: '#f0f0f0', borderRadius: 5, padding: '2px 7px', display: 'inline-block' }}>
                            <Typography style={{ fontSize: 12, color: '#111', fontWeight: 900 }}>{task.reference}</Typography>
                        </Box>
                        <Typography style={{ fontSize: 12, color: dark.text, fontWeight: 500 }}>{renderDate(task.dateEntry, lang)}</Typography>
                    </Box>

                    {/* Bloque central: dispositivo, avería, IMEI, fecha salida */}
                    <Box display="flex" justifyContent="center" mb={0.75}>
                    <Box textAlign="center" style={{ background: dark.surface2, borderRadius: 8, padding: '8px 12px', border: `1px solid ${dark.border}`, display: 'inline-block' }}>
                        <Typography style={{ fontWeight: 700, color: '#fff', fontSize: 15, lineHeight: 1.2, textTransform: 'uppercase' }}>
                            {task.deviceName || t.deviceFallback}
                        </Typography>
                        {task.description && (
                            <Typography style={{ fontSize: 12, color: dark.text, marginTop: 3, textTransform: 'uppercase' }}>
                                {task.description}
                            </Typography>
                        )}
                        {task.imei && (
                            <Typography style={{ fontSize: 12, color: dark.text, marginTop: 3 }}>
                                IMEI: {task.imei}
                            </Typography>
                        )}
                        {task.refClient && (
                            <Typography style={{ fontSize: 12, color: dark.text, marginTop: 2 }}>
                                {t.refClient} {task.refClient}
                            </Typography>
                        )}
                        {task.dateFinish && (
                            <Typography style={{ fontSize: 12, color: dark.text, marginTop: 2 }}>
                                {t.exitDate} {renderDate(task.dateFinish, lang)}
                            </Typography>
                        )}
                    </Box>
                    </Box>

                    {/* Status + Budget + Actions */}
                    <Box display="flex" flexDirection={IS_MOBILE ? 'column' : 'row'} alignItems="center" mt={0.75} style={{ gap: 8, width: '100%' }}>
                        {/* Fila estado + presupuesto */}
                        <Box display="flex" alignItems="center" justifyContent={IS_MOBILE ? 'center' : 'flex-start'} style={{ gap: 8, flexWrap: 'wrap' }}>
                            {StateIcon && <StateIcon style={{ fontSize: 18, color: stateColor, flexShrink: 0 }} />}
                            <Typography style={{ fontSize: 10, color: dark.text, fontWeight: 600, textTransform: 'uppercase' }}>
                                {stateLabel}
                            </Typography>
                            {hasBudget && (
                                <Box display="flex" alignItems="center" style={{ background: '#2d6e4e', borderRadius: 6, padding: '4px 10px', gap: 5, marginLeft: 4, lineHeight: 1 }}>
                                    {signed && <CheckCircleIcon style={{ fontSize: 13, color: '#fff', display: 'block' }} />}
                                    <Typography style={{ fontSize: 11, color: '#fff', fontWeight: 700, lineHeight: 1 }}>
                                        {task.budget} € <span style={{ fontWeight: 400, opacity: 0.8 }}>{task.iva ? t.ivaIncluded : t.ivaExcluded}</span>
                                    </Typography>
                                </Box>
                            )}
                            {!!task.collected && (
                                <Box style={{ background: '#1a3a5c', borderRadius: 6, padding: '4px 8px', marginLeft: 4, display: 'flex', alignItems: 'center' }}>
                                    <LocalShippingIcon style={{ fontSize: 16, color: '#87ceeb' }} />
                                </Box>
                            )}
                            {!signed && hasBudget && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setShowSign(true)}
                                    startIcon={<GestureIcon style={{ fontSize: 14 }} />}
                                    style={{ textTransform: 'none', fontWeight: 600, borderRadius: 6, background: '#f0f0f0', color: '#111', fontSize: 11 }}
                                >
                                    {t.acceptQuote}
                                </Button>
                            )}
                        </Box>
                        {/* Botones de acción */}
                        <Box display="flex" alignItems="center" justifyContent={IS_MOBILE ? 'center' : 'flex-end'} style={{ gap: 6, marginLeft: IS_MOBILE ? 0 : 'auto' }}>
                            {signed && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={handleVerInforme}
                                    disabled={loadingPDF}
                                    startIcon={<DescriptionIcon style={{ fontSize: 14 }} />}
                                    style={{ textTransform: 'none', fontWeight: 600, borderRadius: 6, background: '#f0f0f0', color: '#111', fontSize: 11 }}
                                >
                                    {loadingPDF ? t.generating : t.report}
                                </Button>
                            )}
                            {invoice && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => onOpenInvoice(invoice)}
                                    startIcon={<DescriptionIcon style={{ fontSize: 14 }} />}
                                    style={{ textTransform: 'none', fontWeight: 600, borderRadius: 6, background: '#f0f0f0', color: '#111', fontSize: 11 }}
                                >
                                    {t.invoice} #{invoice.numInvoice}
                                </Button>
                            )}
                        </Box>
                    </Box>


                </CardContent>
            </Card>

            {showSign && (
                <PortalSignModal
                    task={task}
                    lang={lang}
                    onSigned={() => { setJustSigned(true); setShowSign(false) }}
                    onClose={() => setShowSign(false)}
                />
            )}
        </>
    )
}

export default memo(PortalTaskCard)
